/**
 * Shared shell for the two policy documents (`/privacy`, `/terms`).
 *
 * These are legal-ish copy, not product surfaces, so they get one plain
 * layout rather than the card vocabulary the rest of the app uses — and one
 * component so a change to how a document reads happens in one place.
 *
 * `PendingDecision` is the load-bearing piece. Both documents were written
 * from the schema and the code, but several things they need are business
 * decisions a person has to make (the legal entity, retention windows, a
 * contact address). Those are rendered as visible, enumerable blocks rather
 * than invented: `data-policy-todo` lets a test count them, and a reader sees
 * an unfinished section instead of a confident falsehood.
 */

export function PolicyDocument({
  title,
  reviewed,
  children,
}: {
  title: string;
  /** ISO date this document was last read against the code it describes. */
  reviewed: string;
  children: React.ReactNode;
}) {
  return (
    <article className="mx-auto max-w-2xl space-y-8 py-8">
      <header className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
        <p className="label text-xs text-muted-foreground">Last reviewed {reviewed}</p>
      </header>
      {children}
    </article>
  );
}

export function PolicySection({
  id,
  heading,
  children,
}: {
  id: string;
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="space-y-3">
      <h2 className="text-xl font-semibold tracking-tight">{heading}</h2>
      {children}
    </section>
  );
}

export function PolicySubheading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-base font-semibold tracking-tight pt-2">{children}</h3>;
}

export function PolicyText({ children }: { children: React.ReactNode }) {
  return <p className="text-sm leading-relaxed text-muted-foreground">{children}</p>;
}

export function PolicyList({ children }: { children: React.ReactNode }) {
  return (
    <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
      {children}
    </ul>
  );
}

/**
 * An unfinished section, marked as unfinished.
 *
 * Never remove one of these by guessing. Each corresponds to a decision that
 * binds the entity — see docs/policy-revisions.md for the list and who has to
 * settle it.
 */
export function PendingDecision({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <div
      data-policy-todo={id}
      className="rounded-lg border-2 border-dashed border-muted-foreground/50 bg-muted/40 p-4"
    >
      <p className="label text-xs font-semibold">Not decided yet</p>
      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{children}</p>
    </div>
  );
}
